// Styled .xlsx export.
//
// The installed xlsx build (0.16.9 community) writes data only — it drops cell
// styles, so exports came out unformatted. Rather than add a dependency, the
// workbook it produces is unzipped with JSZip (already used by the ID-card
// module), given a proper styles.xml, and rezipped. Only presentation changes;
// the data written by xlsx is untouched.

import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// Four cell formats: 0 default, 1 header, 2 wrapped text, 3 centred.
const STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="0"/>' +
  '<fonts count="2">' +
  '<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>' +
  "</fonts>" +
  '<fills count="3">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FF1E3A5F"/><bgColor indexed="64"/></patternFill></fill>' +
  "</fills>" +
  '<borders count="2">' +
  "<border><left/><right/><top/><bottom/><diagonal/></border>" +
  '<border><left style="thin"><color rgb="FFBFCAD8"/></left><right style="thin"><color rgb="FFBFCAD8"/></right>' +
  '<top style="thin"><color rgb="FFBFCAD8"/></top><bottom style="thin"><color rgb="FFBFCAD8"/></bottom><diagonal/></border>' +
  "</borders>" +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="4">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1">' +
  '<alignment horizontal="center" vertical="center" wrapText="1"/></xf>' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1">' +
  '<alignment horizontal="left" vertical="top" wrapText="1"/></xf>' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1">' +
  '<alignment horizontal="center" vertical="center" wrapText="1"/></xf>' +
  "</cellXfs>" +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '<dxfs count="0"/><tableStyles count="0" defaultTableStyle="TableStyleMedium9"/>' +
  "</styleSheet>";

const HEADER_STYLE = 1;
const WRAP_STYLE = 2;
const CENTER_STYLE = 3;

function columnIndex(letters) {
  var n = 0;
  for (var i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n - 1;
}

// Tag every cell with a format, freeze the header row, and keep the header
// visible while scrolling.
export function styleWorksheetXml(xml, columnStyles) {
  var out = xml.replace(
    '<sheetViews><sheetView workbookViewId="0"/></sheetViews>',
    '<sheetViews><sheetView workbookViewId="0">' +
      '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
      '<selection pane="bottomLeft" activeCell="A2" sqref="A2"/>' +
      "</sheetView></sheetViews>"
  );
  return out.replace(/<c r="([A-Z]+)(\d+)"/g, function (match, col, row) {
    var style;
    if (Number(row) === 1) {
      style = HEADER_STYLE;
    } else {
      style = columnStyles[columnIndex(col)] === "wrap" ? WRAP_STYLE : CENTER_STYLE;
    }
    return '<c r="' + col + row + '" s="' + style + '"';
  });
}

// `columnStylesBySheet` maps a sheet name to an array of "wrap" | "center",
// one entry per column. Anything not marked "wrap" is centred.
export function saveStyledWorkbook(wb, filename, columnStylesBySheet) {
  var raw = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return JSZip.loadAsync(raw)
    .then(function (zip) {
      zip.file("xl/styles.xml", STYLES_XML);
      var sheets = wb.SheetNames.map(function (name, i) {
        var path = "xl/worksheets/sheet" + (i + 1) + ".xml";
        var entry = zip.file(path);
        if (!entry) return Promise.resolve();
        var columnStyles = (columnStylesBySheet && columnStylesBySheet[name]) || [];
        return entry.async("string").then(function (xml) {
          zip.file(path, styleWorksheetXml(xml, columnStyles));
        });
      });
      return Promise.all(sheets).then(function () {
        return zip.generateAsync({
          type: "blob",
          compression: "DEFLATE",
          mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      });
    })
    .then(function (blob) {
      saveAs(blob, filename);
    })
    .catch(function (err) {
      // Never lose the download over a formatting problem — fall back to the
      // plain (unstyled) file xlsx writes itself.
      console.error("[excelStyle] styling failed, saving plain file:", err);
      XLSX.writeFile(wb, filename);
    });
}
